

import { Accessory, AdjacentEntity, Configuration, Connector, Direction, Entity, Material, Module, ModuleSummaryAccessory, ModuleSummaryCustomOption, ModuleSummaryElement, Product, ProductCategory, YUIContext } from "./types/type_def";
import { MaterialLibrary, materialLibrary, ProductLibrary, productLibrary, seriesLibrary } from "./libraries";
import { AppSettings } from "./settings";
import { DragDropHandler } from "./handlers";
import Hammer from "hammerjs";

function isTouchDevice() {
  return 'ontouchstart' in document.documentElement;
}

/**
 * Math Helper
 * */
export class MathHelper {

    static normalizePi(angle: number) {
        var pi = Math.PI;
        while (angle > pi)
            angle -= 2 * pi;
        while (angle < -pi)
            angle += 2 * pi;
        return angle;
    }

    static clamp = function (num, min, max) { return Math.min(Math.max(num, min), max); }

    /** Shortest distance between line segment a1-a2 and line segment b1-b2 on a plane (0 when they cross) */
    static segmentDistance2D(a1: BABYLON.Vector2, a2: BABYLON.Vector2, b1: BABYLON.Vector2, b2: BABYLON.Vector2): number {
        if (MathHelper.segmentsCross2D(a1, a2, b1, b2)) {
            return 0;
        }
        return Math.min(
            MathHelper.pointSegmentDistance2D(a1, b1, b2),
            MathHelper.pointSegmentDistance2D(a2, b1, b2),
            MathHelper.pointSegmentDistance2D(b1, a1, a2),
            MathHelper.pointSegmentDistance2D(b2, a1, a2));
    }

    /** Shortest distance from point p to line segment s1-s2 */
    static pointSegmentDistance2D(p: BABYLON.Vector2, s1: BABYLON.Vector2, s2: BABYLON.Vector2): number {
        const segment = s2.subtract(s1);
        const lengthSquared = segment.lengthSquared();
        const t = lengthSquared === 0 ? 0 : MathHelper.clamp(BABYLON.Vector2.Dot(p.subtract(s1), segment) / lengthSquared, 0, 1);
        return BABYLON.Vector2.Distance(p, s1.add(segment.scale(t)));
    }

    /** Shortest distance between two convex outlines (point lists in order); 0 when they overlap */
    static convexPolygonDistance2D(a: BABYLON.Vector2[], b: BABYLON.Vector2[]): number {
        if (MathHelper.pointInConvexPolygon2D(a[0], b) || MathHelper.pointInConvexPolygon2D(b[0], a)) {
            return 0;
        }
        let distance = Infinity;
        for (let i = 0; i < a.length; i++) {
            for (let j = 0; j < b.length; j++) {
                distance = Math.min(distance, MathHelper.segmentDistance2D(a[i], a[(i + 1) % a.length], b[j], b[(j + 1) % b.length]));
            }
        }
        return distance;
    }

    /** True when point p lies inside (or on) a convex outline (points in order, either direction) */
    static pointInConvexPolygon2D(p: BABYLON.Vector2, polygon: BABYLON.Vector2[]): boolean {
        let sign = 0;
        for (let i = 0; i < polygon.length; i++) {
            const o = polygon[i], q = polygon[(i + 1) % polygon.length];
            const side = (q.x - o.x) * (p.y - o.y) - (q.y - o.y) * (p.x - o.x);
            if (Math.abs(side) < 1e-12) continue;
            if (sign === 0) sign = Math.sign(side);
            else if (Math.sign(side) !== sign) return false;
        }
        return true;
    }

    /** Convex outline around a set of points (counter-clockwise, no duplicate points) */
    static convexHull2D(points: BABYLON.Vector2[]): BABYLON.Vector2[] {
        const sorted = points.slice().sort((a, b) => a.x - b.x || a.y - b.y);
        // Which side of line o-p point q lies on (sign of the cross product)
        const side = (o: BABYLON.Vector2, p: BABYLON.Vector2, q: BABYLON.Vector2) => (p.x - o.x) * (q.y - o.y) - (p.y - o.y) * (q.x - o.x);
        const buildChain = (pts: BABYLON.Vector2[]) => {
            const chain: BABYLON.Vector2[] = [];
            pts.forEach(p => {
                while (chain.length >= 2 && side(chain[chain.length - 2], chain[chain.length - 1], p) <= 1e-9) {
                    chain.pop();
                }
                chain.push(p);
            });
            chain.pop();
            return chain;
        };
        return buildChain(sorted).concat(buildChain(sorted.slice().reverse()));
    }

    /** True when the two segments cross each other (touching is covered by the point distances) */
    private static segmentsCross2D(a1: BABYLON.Vector2, a2: BABYLON.Vector2, b1: BABYLON.Vector2, b2: BABYLON.Vector2): boolean {
        // Which side of line o-p point q lies on (sign of the cross product)
        const side = (o: BABYLON.Vector2, p: BABYLON.Vector2, q: BABYLON.Vector2) => (p.x - o.x) * (q.y - o.y) - (p.y - o.y) * (q.x - o.x);
        return (side(b1, b2, a1) > 0) !== (side(b1, b2, a2) > 0)
            && (side(a1, a2, b1) > 0) !== (side(a1, a2, b2) > 0);
    }


    static getRotationVector(baseAngle: number, additionAngle: number, axis: BABYLON.Axis): BABYLON.Vector3 {

        if (baseAngle === 0) {
            return BABYLON.Vector3.Zero();
        }

        var finalAngle = baseAngle + additionAngle;

        switch (axis) {
            case BABYLON.Axis.X: {
                return new BABYLON.Vector3(finalAngle, 0, 0);
            }
            case BABYLON.Axis.Y: {
                return new BABYLON.Vector3(0, finalAngle, 0);
            }
            case BABYLON.Axis.Z: {
                return new BABYLON.Vector3(0, 0, finalAngle);
            }
        }
    }

    static getUniqueValues(array: Array<any>) {
        var unique = [];
        var distinct = [];
        for (let i = 0; i < array.length; i++) {
            if (!unique[array[i]]) {
                distinct.push(array[i]);
                unique[array[i]] = 1;
            }
        }
        return distinct;
    }

    static getCircleXYZ(angleRadians: number, radius: number, center: BABYLON.Vector3) {
        return new BABYLON.Vector3((Math.cos(angleRadians) * radius) + center.x, 0, (Math.sin(angleRadians) * radius) + center.z)
    }
}

/**
 * Material Helper
 * */

export class MaterialHelper {

    static cloneMaterials(mesh: BABYLON.AbstractMesh) {

        if (mesh.material) {
            let clonedMaterial = mesh.material.clone(mesh.material.id + "_clone");
            mesh.material = clonedMaterial;
        }

        mesh.getChildMeshes(false).forEach(function (m) {
            MaterialHelper.cloneMaterials(m);
        });
    }

}

/**
 * Mesh Helper
 * */

export class MeshHelper {

    static cloneMesh(meshId: string, cloneMesh: BABYLON.TransformNode | BABYLON.AbstractMesh) {

        let clonedMesh = cloneMesh.clone(meshId + "_clone", null, false) as BABYLON.AbstractMesh;
        clonedMesh.id = clonedMesh.name;
        clonedMesh.scaling.z = -1;
        clonedMesh.setEnabled(true);

        MaterialHelper.cloneMaterials(clonedMesh);

        return clonedMesh;

    }

    static instanceMesh(meshId: string, mesh: BABYLON.Mesh, root: BABYLON.TransformNode) {

        let instanceMesh = mesh.createInstance(meshId + "_instance");
        instanceMesh.id = instanceMesh.name;
        instanceMesh.setEnabled(true);

        return instanceMesh;
    }
}

/**
 * Conversion Helper
 * */

export class ConversionHelper {

    static scale(value: any, factor: number) {
        if (typeof value === "number") {
            return value * factor;
        }
        if (value instanceof BABYLON.Vector3) {
            return value.scale(factor);
        }
    }

    static toVector3(value: any): BABYLON.Vector3 {
        if (value instanceof BABYLON.Vector3) {
            return value;
        }
        if (value instanceof Object) {
            if (value.x !== undefined &&
                value.y !== undefined &&
                value.z !== undefined) {
                return new BABYLON.Vector3(value.x, value.y, value.z);
            }
        }
        return new BABYLON.Vector3(value._x, value._y, value._z);
    }
}

/*
 * Data Helper
 * */

export class DataHelper {
    static find = (array, id) => {
        let result;
        array.some(o => result = o.id === id
            ? o
            : DataHelper.find(o.options || [], id)
        )
        return result;
    }

    static findDuplicate(arr) {
        arr.sort();

        let currentMostRepeatedNumber = arr[0];
        let currentNumberOfTimeRepeated = 1;

        for (var i = 0; i < arr.length; i++){

            //We initiate the current number we'll check in this iteration
            let currentRepeat = arr[i];
            let amountOfRepetition = 1;

            //We start a for to check the amount of times the number is repeated
            for(var y= 0; y < arr.length;y++){
                
                if(currentRepeat === arr[y]){
                    amountOfRepetition++;
                }  
            }
        
            if(amountOfRepetition > currentNumberOfTimeRepeated ){
                    currentNumberOfTimeRepeated = amountOfRepetition;
                    currentMostRepeatedNumber = currentRepeat ;
            }
        }

        return { value: currentMostRepeatedNumber, repeated: currentNumberOfTimeRepeated}
       // return "The most repeated number is " +currentMostRepeatedNumber + " , and he is repetead " + currentNumberOfTimeRepeated +" amount of times"
    }
}

/**
 *  Date Helper
 */

// @ts-ignore

export class FormatHelper {

    static numberformat = new Intl.NumberFormat('en-GB');

    static formatDate = (date: Date) => {
        // @ts-ignore
        var dateFormatted = luxon.DateTime.fromISO(date);
        return dateFormatted.toFormat('dd.MM.yyyy hh:mm');
    }

    static formatNumber = (value: number) => {
        return FormatHelper.numberformat.format(value);
    }
}

/**
 * Product Helper
 * */

export class ProductHelper {

    static getFormattedString(product: Product | Accessory, values: Array<string>) {

        let joinableValues = [];

        values.forEach((key, index) => {
            if (product[key]) {
                joinableValues.push(product[key]);
            }
        });

        return joinableValues.join(" x ");

    }
}

/**
 * Module Helper
 * */

export class ModuleHelper {

    scene: BABYLON.Scene;
    scaleFactor: number;
    entityHelper: EntityHelper;

    async generateModuleSummaryList(module: Module, configuration: Configuration): Promise<{ elements: ModuleSummaryElement[], customOptions: ModuleSummaryCustomOption[], accessories: ModuleSummaryAccessory[] }> {

        let self = this;

        let elements: ModuleSummaryElement[] = [];
        let accessories: ModuleSummaryAccessory[] = [];
        let customOptions: ModuleSummaryCustomOption[] = [];     

       const series = await  seriesLibrary.fetchSeriesByName(configuration.series);

            let leg = seriesLibrary.getSeriesLegPackage(series, configuration.data.leg);

            if (!configuration) {
                return { accessories: [], elements: [], customOptions: [] };
            }

            let currentMaterial = materialLibrary.getMaterialByProductNumber(configuration.data.material) as Material;

            let sortedEntityIds = module?.entities.filter(x => productLibrary.getProductCategory(x.category) === "UPHOLSTRY").map(x => x.id) ?? [];

            for (let entityId of sortedEntityIds) {

                var entity = module.entities.find(x => x.id === entityId);
                var product = productLibrary.getProductByProductNo(entity.productNo)

                if (!product)
                    continue;

                var productVariant = productLibrary.getProductVariantByConnectionType(product, self.getEntityConnectionType(module, entity))
                var priceItem = productLibrary.getProductPrice(product.productNo, currentMaterial.priceGroup);

                if (!productVariant) {
                    productVariant = productLibrary.getFirstProductVariant(product);
                }
                        
                let extras: { name: string, productNo: string, price: number, optionId: string, optionValue: string }[] = [];
                
                leg.priceGroups.forEach(priceGroup => {
                    var option = series.customOptions.find(e => e.priceLists.some(e => e.priceGroup == priceGroup));

                var pricelist = option.priceLists.find(e => e.productType == "Leg Package" && e.priceGroup == priceGroup && e.items.includes(productVariant.name));

                    if (pricelist) {
                        const index = customOptions.findIndex(e => e.productNo == option.id);

                    if (index >= 0) {
                        customOptions[index].totalprice += pricelist.price.price ?? 0;
                        customOptions[index].amount += 1;
                    } else {
                        customOptions.push({
                            amount: 1,
                            currency: pricelist.price.currency ?? "",
                            productNo: option.name,
                            totalprice: pricelist.price.price ?? 0,
                            name: pricelist.description
                        })
                    }
                }
                
            });

                series.customOptions.forEach(option => {
                    if (option.perModule) {
                        const selection = entity.customOptions?.find(e => e.id == option.id);
                        if (selection) {
                            var pricelist = option.priceLists.find(e => e.productType == "Frame Cushion Item" && e.items.includes(productVariant.name));

                            if (pricelist) {
                                extras.push({ name: option.name, productNo: option.productNumber, price: pricelist.price.price ?? 0, optionId: option.id, optionValue: option.id });
                            }
                        }
                    } else {
                        const selection = configuration.data.customOptions?.find(e => e.id == option.id);
                        if (selection) {
                            var pricelists = option.priceLists.filter(e => ["Frame Cushion Item", "Leg Package"].includes(e.productType));
                            var pricelist = pricelists.find(e => e.items.includes(productVariant.name));

                            if (pricelist) {
                                const index = customOptions.findIndex(e => e.productNo == option.id);

                            if (index >= 0) {
                                customOptions[index].totalprice += pricelist.price.price ?? 0;
                                customOptions[index].amount += 1;
                            } else {
                                customOptions.push({
                                    amount: 1,
                                    currency: pricelist.price.currency ?? "",
                                    productNo: option.name,
                                    totalprice: pricelist.price.price ?? 0,
                                    name: pricelist.description
                                })
                            }
                        }
                    }
                }
            });

                const element = {
                    axProductNo: productVariant.axProductNo,
                    variantName: productVariant.name,
                    variantProductNo: productVariant.productNo,
                    productNo: product.productNo,
                    customerInfoText: productVariant.customerInfoText,
                    currency: priceItem.currency,
                    price: priceItem.price,
                    legPackageId: productVariant.legPackageID,
                    amount: 1,
                    entity: entityId,
                    extras
                }

                if (elements.filter(x => x.variantProductNo === element.variantProductNo).length > 0) {
                    var existingElement = elements.find(x => x.variantProductNo === element.variantProductNo);
                    existingElement.amount += 1;
                    existingElement.price += existingElement.price
                } else {
                    elements.push(element);
                }

                productVariant.recommendedCushions.forEach(cushion => {
                    const addOn = series.accessories.find(e => e.name == cushion.name);
                    if (addOn) {
                        const index = accessories.findIndex(e => e.productNo == addOn.productNumber);

                        if (index >= 0) {
                            accessories[index].amount += cushion.quantity;
                        } else {
                            accessories.push({
                                name: cushion.name,
                                amount: cushion.quantity,
                                currency: addOn.priceLists[0]?.currency ?? "",
                                productNo: addOn.productNumber,
                                price: addOn.priceLists[0]?.prices[currentMaterial.priceGroup] ?? 0,
                                extras: [],
                            })
                        }
                    }
                });
            };


            if (configuration.data.addons) {
                configuration.data.addons.forEach(a => {
                    const addOn = series.accessories.find(e => e.productNumber == a.productNo);
                    if (addOn) {
                        const index = accessories.findIndex(e => e.productNo == addOn.productNumber);

                        if (index >= 0) {
                            accessories[index].amount = Math.max(0, accessories[index].amount + a.amount);
                        } else if (a.amount > 0) {
                            accessories.push({
                                name: addOn.name,
                                amount: a.amount,
                                currency: addOn.priceLists[0]?.currency ?? "",
                                productNo: addOn.productNumber,
                                price: addOn.priceLists[0]?.prices[currentMaterial.priceGroup] ?? 0,
                                extras: [],
                            })
                        }
                    }
                });
            }

            accessories.forEach(accessory => {

                series.customOptions.forEach(option => {
                    const selection = configuration.data.customOptions?.find(e => e.id == option.id);
                    if (selection) {
                        var pricelist = option.priceLists.find(e => e.productType == "Accessories" && e.accessories.includes(accessory.name));

                        if (pricelist) {
                            accessory.extras.push({
                                name: option.name,
                                productNo: option.productNumber,
                                price: pricelist.price.price ?? 0,
                                optionId: option.id,
                                optionValue: option.id
                            });
                        }
                    }
                })
            })
        

        return { elements, accessories, customOptions };
    }

    getCurrency(module: Module, configuration: Configuration) {

        if (!productLibrary.productsCategories)
            return;

        var product = productLibrary.productsCategories[0].products[0];
        let currentMaterial = materialLibrary.getMaterialByProductNumber(configuration.data.material) as Material;
        var priceItem = productLibrary.getProductPrice(product.productNo, currentMaterial.priceGroup);
        return priceItem.currency;
    }

    async getTotalModulePrice(module: Module, configuration: Configuration) {
        var summaryList = await this.generateModuleSummaryList(module, configuration);
        var totalPrice = 0;
        var currency = this.getCurrency(module, configuration);

        for (let i = 0; i < summaryList.elements.length; i++) {
            const element = summaryList.elements[i];

            totalPrice += element.price;
            totalPrice += element.extras.reduce((p, n) => { return p + n.price }, 0);
        }
        for (let i = 0; i < summaryList.customOptions.length; i++) {
            const customOption = summaryList.customOptions[i];

            totalPrice += customOption.totalprice;
        }
        for (let i = 0; i < summaryList.accessories.length; i++) {
            const accessory = summaryList.accessories[i];

            totalPrice += accessory.price * accessory.amount;
            totalPrice += accessory.extras.reduce((p, n) => { return p + n.price }, 0) * accessory.amount;
        }

        return { totalPrice, currency };
    }

    generateSortedEntityIds(module: Module, entity: Entity, traversed: Array<string>) {

        let self = this;

        if (!entity)
            return traversed;

        if (traversed.length === 0) {
            traversed.push(entity.id);
        }

        let currentConnections = entity.connections;
        currentConnections.forEach(connection => {

            var nextEntity = module.entities.find(x => x.id === connection.entityId);
            if (!nextEntity)
                return traversed;

            if (!traversed.includes(nextEntity.id)) {
                entity = nextEntity;
                traversed.push(entity.id);
                return self.generateSortedEntityIds(module, nextEntity, traversed)
            }

        });

        return traversed;
    }


    getBaseConnectorName(connectorName: string) {

        if (connectorName.split("_").length > 2) {
            connectorName = connectorName.substring(0, connectorName.lastIndexOf("_"));
        }
        return connectorName;
    }

    getEntityByID(module: Module, id: string): Entity {
        return module.entities.find(e => { return e.id === id });
    }

    getEntityConnectionType(module: Module, entity: Entity) {

        let self = this;

        var connectionType = "";

        if (!entity) {
            return connectionType;
        }

        var entityProduct = productLibrary.getProductByProductNo(entity.productNo);

        if (!entityProduct)
            return connectionType;

        const adjacentEntities = self.entityHelper.getAdjacentEntities(module, entity, self.scene);
        const childDirections = adjacentEntities.map(x => x.direction);
        const directions = childDirections.filter((x,i,arr) => arr.indexOf(x) === i).sort().toString()       

        var entityConnectionTypes = entityProduct.cushionPackages.find(x => x.isDefault).variants.map(x => x.connectionType)


        var connections = entity.connections.map(function (c) { return self.getBaseConnectorName(c.id) });

        let hasConnections = (arr, target) => target.every(v => arr.includes(v));

        if (connections.length === 0) {
            connectionType = "CT00";
        }
        if (hasConnections(connections,["CT01_L"]) || hasConnections(connections,["CT01_R"])) {
            connectionType = "CT01";
        }
        if (hasConnections(connections,["CT03"])) {
            connectionType = "CT03";
        }
        if (hasConnections(connections,["CT04"])) {
            connectionType = "CT04";
        }
        if (hasConnections(connections,["CT05"])) {
            connectionType = "CT05";
        }
        if (hasConnections(connections,["CT06"])) {
            connectionType = "CT06";
        } 
        if (hasConnections(connections,["CT11"])) {
            connectionType = "CT11";
        } 
        if (hasConnections(connections,["CT37"])) {
            connectionType = "CT37";
        }                  
        if (hasConnections(connections,["CT03","CT04"])) {
            connectionType = "CT02";
        } 
        if (hasConnections(connections,["CT05","CT06"])) {
            connectionType = "CT07";
        }       
        if (hasConnections(connections,["CT05","CT01_R"])) {
            connectionType = "CT08";
        }
        if (hasConnections(connections,["CT06","CT01_L"])) {
            connectionType = "CT09";
        }       
        if (hasConnections(connections,["CT01_L","CT01_R"])) {
            connectionType = "CT10";
        }
        if (hasConnections(connections,["CT11","CT03"])) {
            connectionType = "CT25";
        }
        if (hasConnections(connections,["CT11","CT04"])) {
            connectionType = "CT26";
        }
        if (hasConnections(connections,["CT11","CT37"])) {
            connectionType = "CT36";
        }
        if (hasConnections(connections,["CT03","CT37"])) {
            connectionType = "CT38";
        }
        if (hasConnections(connections,["CT04","CT37"])) {
            connectionType = "CT39";
        }
        if (hasConnections(connections,["CT11","CT03","CT37"])) {
            connectionType = "CT34";
        }
        if (hasConnections(connections,["CT11","CT04","CT37"])) {
            connectionType = "CT35";
        }
        if (hasConnections(connections,["CT03","CT04","CT37"])) {
            connectionType = "CT40";
        }
        if (hasConnections(connections,["CT03","CT04","CT11"])) {
            connectionType = "CT41";
        }
        if (hasConnections(connections,["CT03","CT04","CT11","CT37"])) {
            connectionType = "CT12";
        }

        switch (entity.category) {

            case "CENTER_SECTION":
            case "CENTER_SECTION_LEFT_SHORTBACK":
            case "CENTER_SECTION_RIGHT_SHORTBACK":
            case "CENTER_SECTION_WO_BACK": {

            }
                break;
            case "FOOTSTOOL": {

            }
                break;
            case "CLOSING_SECTION_LEFT":
            case "CLOSING_SECTION_LEFT_SHORTBACK":
            case "CLOSING_SECTION_LEFT_WO_ARM": {

            }
                break;
            case "CLOSING_SECTION_RIGHT":
            case "CLOSING_SECTION_RIGHT_SHORTBACK":
            case "CLOSING_SECTION_RIGHT_WO_ARM": {

            }
                break;
            case "CHAISELONG_LEFT":
            case "CHAISELONG_LEFT_WO_ARM": {

            }
                break;
            case "CHAISELONG_RIGHT":
            case "CHAISELONG_RIGHT_WO_ARM": {

            }
                break;
            case "CORNER_SECTION":
            case "CLOSING_CORNER_SECTION_LEFT":
            case "CLOSING_CORNER_SECTION_RIGHT": {

            }
                break;
            case "SOFA":
            case "SOFA_LEFT":
            case "SOFA_RIGHT":
            case "SOFA_SECTIONAL": {

            }
                break;
            case "ARM":
            case "ARM_LEFT":
            case "ARM_RIGHT": {

                if ([148882,157547,157546].includes(entityProduct.series)) {

                    if (connections.includes("CT03")) {
                        connectionType = "CT03";
                    }
                    if (connections.includes("CT04")) {
                        connectionType = "CT04";
                    }
                    if (connections.includes("CT03") && connections.includes("CT04")) {
                        connectionType = "CT02";
                    }
                    if (connections.includes("CT44")) {
                        connectionType = "CT44";
                    }
                    if (connections.includes("CT45")) {
                        connectionType = "CT45";
                    }
                    if (connections.includes("CT44") && connections.includes("CT45")) {
                        connectionType = "CT46";
                    }
                    if (connections.includes("CT44_center")) {
                        connectionType = "CT47";
                    }
                    if (connections.includes("CT45_center")) {
                        connectionType = "CT48";
                    }
                    if (connections.includes("CT44_center") && connections.includes("CT45_center")) {
                        connectionType = "CT49";
                    }
                }
            }
                break;
            case "BACK": {

                if ([148882,157547,157546].includes(entityProduct.series)) {
                   
                    if (connections.includes("CT37") && connections.includes("CT36")) {
                        connectionType = "CT36";
                    }
                    if (connections.includes("CT37") && connections.includes("CT38") && connections.includes("CT39")) {
                        connectionType = "CT40";
                    }
                    if (connections.includes("CT36") && connections.includes("CT37") && connections.includes("CT38")) {
                        connectionType = "CT34";
                    }
                    if (connections.includes("CT36") && connections.includes("CT37") && connections.includes("CT39")) {
                        connectionType = "CT35";
                    }
                    if (connections.includes("CT36") && connections.includes("CT37") && connections.includes("CT38") && connections.includes("CT39")) {
                        connectionType = "CT12";
                    }
                }

            }
                break;
            case "SEATFRAME": {

                if ([148882,157547,157546].includes(entityProduct.series)) {

                    if (directions === "LEFT") {
                        connectionType = "CT03";
                    } 
                    if (directions === "RIGHT") {
                        connectionType = "CT04";
                    } 
                    if (directions === "LEFT,RIGHT") {
                        connectionType = "CT02";
                    }               
                    if (directions === "BACK") {
                        connectionType = "CT11";
                    } 
                    if (directions === "FRONT") {
                        connectionType = "CT37";
                    }
                    if (directions === "BACK,LEFT") {
                        connectionType = "CT25";
                    }
                    if (directions === "BACK,RIGHT") {
                        connectionType = "CT26";
                    }
                    if (directions === "BACK,FRONT,LEFT") {
                        connectionType = "CT34";
                    }
                    if (directions === "BACK,FRONT,RIGHT") {
                        connectionType = "CT34";
                    }
                    if (directions === "BACK,FRONT") {
                        connectionType = "CT36";
                    }
                    if (directions === "FRONT,LEFT") {
                        connectionType = "CT38";
                    }
                    if (directions === "FRONT,RIGHT") {
                        connectionType = "CT39";
                    }
                    if (directions === "FRONT,LEFT,RIGHT") {
                        connectionType = "CT40";
                    }
                    if (directions === "FRONT,LEFT,RIGHT") {
                        connectionType = "CT40";
                    }
                    if (directions === "BACK,LEFT,RIGHT") {
                        connectionType = "CT41";
                    }
                    if (directions === "BACK,FRONT,LEFT,RIGHT") {
                        connectionType = "CT12";
                    } 
                }
             
            }
                break;
        }

        if (!entityConnectionTypes.includes(connectionType)) {
            connectionType = entityConnectionTypes[0];
        }

        return connectionType;
    }
}



/* *
 * Leg Selector 
 * */

export class MaterialSelector {

    YUI: YUIContext
    element: any
    materialLibrary: MaterialLibrary
    scrollView: any
    configuration: Configuration
    materialGroup: string
    selectCallBack: any
    selectGroupCallback: any

    constructor(YUI: YUIContext, materialLibrary: MaterialLibrary, element: any) {
        this.YUI = YUI;
        this.materialLibrary = materialLibrary;
        this.element = element;

        this.init(YUI);
    }

    public init(YUI: YUIContext) {

        let self = this;

        this.scrollView = new YUI.ScrollView({
            srcNode: this.element.one(".selector__items"),
            axis: "y",
            height: 100,
            flick: {
                minDistance: 10,
                minVelocity: 0.3,
                axis: "y"
            }
        });

        this.scrollView.render();

        window.addEventListener("resize", function (e) {
            self.resize();
        });

        this.element.delegate("click", function (e) {

            if (Math.abs(self.scrollView.lastScrolledAmt) < 2) {
                var elem = e.currentTarget;

                self.element.all(".selector__items li").each(function (e) {
                    e.removeClass("is-active");
                });

                elem.addClass("is-active");

                var materialProductNumber = elem.getData("id");

                self.configuration.data["material"] = materialProductNumber;
                var currentMaterial = materialLibrary.getMaterialByProductNumber(materialProductNumber);

                var folderName = currentMaterial.type + "/" + currentMaterial.group;
                let selectedMaterialElem = self.element.one(".material-selector__material__image");
                selectedMaterialElem.setStyle("background-image", "url('" + AppSettings.Paths.BaseMaterialsPath + folderName + "/" + currentMaterial.productNumber + "_thumb.jpg')");
                let selectedName = self.element.one(".material-selector__material__name");
                selectedName.setHTML(currentMaterial.name + "(pricegroup: " + currentMaterial.priceGroup + ")");

                if (typeof (self.selectCallBack) === "function") {
                    self.selectCallBack();
                }
            }

        }, ".selector__items li");

        this.element.delegate("change", function (e) {

            self.configuration.data.options["removableCover"] = !self.configuration.data.options["removableCover"];
            window.dispatchEvent(new Event("saveconfigurations"));

        }, ".cover input");

        this.element.delegate("click", function (e) {

            e.preventDefault();

            if (typeof (self.selectGroupCallback) === "function") {
                self.selectGroupCallback();
            }

        }, ".material-selector__items__group__select");

    }

    public setConfiguration(configuration: Configuration) {
        this.configuration = configuration;
        this.update();
    }

    public setMaterialGroup(materialGroup) {
        this.materialGroup = materialGroup;
        this.update();
    }

    public resize() {

        this.scrollView.scrollTo(0, 0);
        this.scrollView._uiDimensionsChange();

        let contentElem = this.scrollView.get("contentBox");

        if (contentElem.getDOMNode().offsetHeight >= contentElem.getDOMNode().scrollHeight) {
            this.scrollView.scrollbars.hide();
            this.scrollView.disable();
        } else {
            this.scrollView.enable();
            this.scrollView.scrollbars.show();
            this.scrollView._uiDimensionsChange();
        }
    }

    public empty() {
        let itemList = this.element.one(".selector__items");
        itemList.empty();
    }

    public update() {

        let self = this;
        self.empty();

        var material = this.materialLibrary.materials[0];
        if (material) {
            var group = material.group;

            if (self.materialGroup) {
                group = self.materialGroup;
            }

            if (self.configuration) {
                if (self.configuration.data["material"]) {
                    material = materialLibrary.getMaterialByProductNumber(self.configuration.data["material"]);
                } else {
                    self.configuration.data["material"] = material.productNumber;
                }
            }

            if (!self.materialGroup) {
                group = material.group;
            }

            var folderName = material.type + "/" + material.group;
            let selectedMaterialElem = self.element.one(".material-selector__material__image");
            selectedMaterialElem.setStyle("background-image", "url('" + AppSettings.Paths.BaseMaterialsPath + folderName + "/" + material.productNumber + "_thumb.jpg')");
            let selectedName = self.element.one(".material-selector__material__name");
            selectedName.setHTML(material.name + " (pricegroup: " + material.priceGroup + ")");

            var groupedMaterials = materialLibrary.materials.filter(function (material) { return material.group === group });

            let itemsElem = this.element.one(".selector__items");

            let materialElem = this.element.one(".material-selector__material__name");
            materialElem.setHTML(material.name + " (pricegroup: " + material.priceGroup + ")");

            let groupElem = this.element.one(".material-selector__group__name");
            groupElem.setHTML(self.materialGroup);

            groupedMaterials.forEach(function (groupedMaterial) {

                var materialNode = self.YUI.Node.create("<li>");
                materialNode.setAttribute("data-id", groupedMaterial.productNumber);

                if (material.productNumber === groupedMaterial.productNumber) {
                    materialNode.addClass("is-active");
                }

                var itemContent = self.YUI.Node.create("<div>");
                itemContent.setStyle("background-image", "url('" + AppSettings.Paths.BaseMaterialsPath + groupedMaterial.type + "/" + groupedMaterial.group + "/" + groupedMaterial.productNumber + "_thumb.jpg')");

                materialNode.appendChild(itemContent);
                itemsElem.appendChild(materialNode);

            });
        }

        //Has Cover?

        this.resize();
    }
}

/* *
 * Product Selector
 * */

export class ProductSelector {

    YUI: YUIContext
    element: any
    material: any
    dragdropHandler: DragDropHandler
    productLibrary: ProductLibrary
    selectedCategory: ProductCategory

    constructor(YUI: YUIContext, dragdropHandler: DragDropHandler, productLibrary: ProductLibrary, element: any) {
        this.YUI = YUI;
        this.dragdropHandler = dragdropHandler;
        this.productLibrary = productLibrary;
        this.element = element;        
        this.material = {} as Material;

        this.init(YUI);
    }

    public init(YUI: YUIContext) {

        let self = this;

        var navScrollLeft = 0;
        var itemsScrollLeft = 0;

        const navElement = document.querySelector('.selector__tabs__nav')! as HTMLElement;
        const itemsElement = document.querySelector('.selector__items')! as HTMLElement;

        if (!isTouchDevice()) {

            const navGestures = new Hammer(navElement);
            navGestures.get('pan').set({ direction: Hammer.DIRECTION_HORIZONTAL });
            navGestures.on('panend', (e) => {              
                navScrollLeft = navElement.scrollLeft;               
            });
            navGestures.on('panmove', (e) => {               
                if ((e.srcEvent as PointerEvent).buttons > 0) {
                    navElement.scrollLeft = navScrollLeft - e.deltaX;       
                }
            });   

            const itemsGestures = new Hammer(itemsElement);
            itemsGestures.get('pan').set({ direction: Hammer.DIRECTION_HORIZONTAL });
            itemsGestures.on('panend', (e) => {              
                itemsScrollLeft = itemsElement.scrollLeft;               
            });
            itemsGestures.on('panmove', (e) => {               
                if ((e.srcEvent as PointerEvent).buttons > 0) {
                    itemsElement.scrollLeft = itemsScrollLeft - e.deltaX;       
                }
            });  

        } else {
            navElement.style.touchAction = 'auto'; 
            itemsElement.style.touchAction = 'auto'; 
        }

        window.addEventListener("resize", function (e) {
            self.resize();
        });

        this.element.delegate("click", function (e) {

                var target = e.currentTarget;
                var category = target.getAttribute("data-category");

                self.selectedCategory = self.productLibrary.productsCategories.find(x => x.type === category);
                self.update();

        }, ".selector__tabs__nav li");
    }

    public resize() {



    }

    public reset() {

        this.selectedCategory = this.productLibrary.productsCategories.filter(function (pc) { return pc.products })[0];
    }

    public empty() {

        let navList = this.element.one(".selector__tabs__nav");
        navList.empty();

        let itemList = this.element.one(".selector__items");
        itemList.empty();
    }

    public update() {

        let self = this;
        self.empty();

        var productCategories = this.productLibrary.productsCategories.filter(function (product) { return productLibrary.getProductCategory(product.type) === "UPHOLSTRY" });

        let navElem = this.element.one(".selector__tabs__nav");
        productCategories.forEach(function (category) {
            var productNode = self.YUI.Node.create("<li class='selector__tabs__navitem'>");
            productNode.setAttribute("data-type", "category");
            productNode.setAttribute("data-category", category.type);
            productNode.setAttribute("data-name", category.name);

            if (self.selectedCategory === category) {
                productNode.addClass("active")
            }

            productNode.setHTML(category.name);
            navElem.appendChild(productNode);
        });

        let itemsElem = this.element.one(".selector__items");
        this.element.addClass("show");

        var categoryProducts = productCategories.map(x => {
            return {
                id: x.id,
                type: x.type,
                name: x.name,
                productNo: null,
                products: x.products
            }
        }) as any[];

        if (self.selectedCategory) {
            let filteredProducts = productCategories.filter(function (productCategory) { return productCategory.type === self.selectedCategory.type })[0];
            categoryProducts = filteredProducts.products;
        } else {
            self.selectedCategory = productCategories[0];
        }

        categoryProducts.forEach(function (product) {

            var categoryProductNode = self.YUI.Node.create("<li class='selector__items__item product'>");
            if (categoryProductNode.getDOMNode() !== null) {

                categoryProductNode.setAttribute("data-type", "product");
                categoryProductNode.setAttribute("data-productnumber", product.productNo);

                var wrapperElem = self.YUI.Node.create("<div class='selector__items__item__wrapper'>");

                var imageElem = self.YUI.Node.create("<div class='selector__items__item__image'>");
                imageElem.setAttribute("data-productnumber", product.productNo);

                var nameElem = self.YUI.Node.create("<div class='selector__items__item__name'>");

                var productImage = self.YUI.Node.create("<img>");
                productImage.setAttribute("src", product.thumbnail);
                imageElem.appendChild(productImage);

                if (productImage) {
                    productImage.getDOMNode().onload = function (e) {
                        var currentCategoryProductNode = self.element.one(".selector__items__item.product[data-productnumber='" + product.productNo + "']")
                        if (currentCategoryProductNode.getDOMNode() !== null)
                            currentCategoryProductNode.addClass("show");
                    }
                    productImage.getDOMNode().onerror = function (e) {
                        productImage.setAttribute("src", "/assets/series/1/products/171/thumbnail");
                    }
                }

                var productType = self.YUI.Node.create("<div>");
                productType.setHTML(product.name);
                nameElem.appendChild(productType);

                wrapperElem.appendChild(imageElem);
                wrapperElem.appendChild(nameElem);
                categoryProductNode.appendChild(wrapperElem);
                
                self.dragdropHandler.createDraggable(product.productNo, imageElem.getDOMNode());

                itemsElem.appendChild(categoryProductNode);
            }
        });

        this.resize();
    }
}

/*
* Entity Helper
**/

export class EntityHelper {

    getEntityBoundingBox = function (entity: Entity, scene: BABYLON.Scene) {
        var entityNode = scene.getTransformNodeByUniqueId(entity.uniqueId);
        var entityBounds = entityNode.getChildMeshes(true, function (m) { return m.id === "entitybounds" })[0];
        var bv = entityBounds.getHierarchyBoundingVectors();
        var bb = new BABYLON.BoundingBox(bv.min, bv.max);
        return bb;
    }


    getNeighboringEntities = function(module: Module, entity: Entity, scene: BABYLON.Scene) {

        const entityNode = scene.getTransformNodeById(entity.id);
        entityNode.computeWorldMatrix(true);
        
        const availableEntities = module.entities.filter(function (e: Entity) {            
            return productLibrary.getProductCategory(e.category) === "UPHOLSTRY" && e.id !== entity.id && e.connectors.length > 0
        });   
        
        const neighbouringEntities: { entity: Entity, connector: Connector, direction: Direction, isConnected: boolean }[] = []

        for (let connector of entity.connectors) {

            const connectorMesh = entityNode.getChildMeshes(true, x => x.name === connector.id)[0];
            connectorMesh.computeWorldMatrix(true);

            var connection = entity.connections.find(x => x.id === connector.id);
            if (connection) {
                const connectionEntity = module.entities.find(x => x.id === connection.entityId);
                if (neighbouringEntities.filter(x => x.direction === connector.direction && x.connector.id === connector.id).length === 0) {
                    neighbouringEntities.push({ entity: connectionEntity, connector: connector, direction: connector.direction, isConnected: true});
                    continue;
                }
            }        
             
            for (let availableEntity of availableEntities) {

                const availableEntityNode = scene.getTransformNodeById(availableEntity.id);
                availableEntityNode.computeWorldMatrix(true);
                
                const availableEntityBounds = availableEntityNode.getChildMeshes(false, function (m) { return m.id === "entitybounds" })[0];
                if (availableEntityBounds.intersectsPoint(connectorMesh.absolutePosition)) {
                    if (neighbouringEntities.filter(x => x.direction === connector.direction && x.connector.id === connector.id).length === 0) {
                        neighbouringEntities.push({ entity: availableEntity, connector: connector, direction: connector.direction, isConnected: false});
                        continue;
                    }
                    
                } 

                const bv = availableEntityNode.getHierarchyBoundingVectors(true);
                const bb = new BABYLON.BoundingBox(bv.min, bv.max);

                if (bb.intersectsPoint(connectorMesh.absolutePosition) && neighbouringEntities.filter(x => x.entity.id === availableEntity.id).length === 0) {
                    if (neighbouringEntities.filter(x => x.direction === connector.direction && x.connector.id === connector.id).length === 0) {
                        neighbouringEntities.push({ entity: availableEntity, connector: connector, direction: connector.direction, isConnected: false});
                        continue;
                    }                    
                }

                for (let availableEntityConnectors of availableEntity.connectors) {
                    const availableConnectorMesh = availableEntityNode.getChildMeshes(true, x => x.name === availableEntityConnectors.id)[0];
                    if (BABYLON.Vector3.Distance(connectorMesh.absolutePosition,availableConnectorMesh.absolutePosition) <= 0.01) {
                        if (neighbouringEntities.filter(x => x.direction === connector.direction && x.connector.id === connector.id).length === 0) {
                            neighbouringEntities.push({ entity: availableEntity, connector: connector, direction: connector.direction, isConnected: false});
                            continue;
                        }
                    }
                    
                }
           }              
        }

        return neighbouringEntities;
    }

    getAdjacentEntities = function (module: Module, entity: Entity, scene: BABYLON.Scene) {

        const adjacentEntities: AdjacentEntity[] = [];

        if (entity.connections.length === 0)
            return adjacentEntities;

        const entityNode = scene.getTransformNodeByUniqueId(entity.uniqueId);

        if (!entityNode)
            return adjacentEntities;

        entityNode.computeWorldMatrix(true);
        const entityBounding = entityNode.getChildMeshes(false, function (m) { return m.id === "entitybounds" })[0];
          entityBounding.computeWorldMatrix(true);
        const bv1 = entityBounding.getHierarchyBoundingVectors();
        const bb1 = new BABYLON.BoundingBox(bv1.min, bv1.max);      

        let rotationMatrix = BABYLON.Matrix.RotationYawPitchRoll(entity.rotation._y, entity.rotation._x, entity.rotation._z);
        
        const filteredEntities = module.entities.filter(x => productLibrary.getProductCategory(x.category) === "UPHOLSTRY");
        for (let i = 0; i < filteredEntities.length; i++) {

            const filteredEntity = filteredEntities[i];
            const filteredEntityNode = scene.getTransformNodeById(filteredEntity.id);

            if (filteredEntity.connections.length === 0)
                continue;           

            if (!filteredEntityNode)
                continue;

            const filteredEntityBounding = filteredEntityNode.getChildMeshes(false, function (m) { return m.id === "entitybounds" })[0];

            let checkPos = bb1.center;         

            if (entity.id !== filteredEntity.id) {

                const front = filteredEntityBounding.getBoundingInfo().intersectsPoint(checkPos.add(BABYLON.Vector3.TransformCoordinates(new BABYLON.Vector3(0, 0, bb1.extendSize.z + 0.2), rotationMatrix)))
                const back = filteredEntityBounding.getBoundingInfo().intersectsPoint(checkPos.add(BABYLON.Vector3.TransformCoordinates(new BABYLON.Vector3(0, 0, -bb1.extendSize.z - 0.2), rotationMatrix)))

                let left = filteredEntityBounding.getBoundingInfo().intersectsPoint(checkPos.add(BABYLON.Vector3.TransformCoordinates(new BABYLON.Vector3(bb1.extendSize.x + 0.2, 0, 0), rotationMatrix)))
                if (!left) {
                    left = filteredEntityBounding.getBoundingInfo().intersectsPoint(checkPos.add(BABYLON.Vector3.TransformCoordinates(new BABYLON.Vector3(bb1.extendSize.x + 0.2, 0, bb1.extendSize.z - 0.2), rotationMatrix)))
                }
                if (!left) {
                    left = filteredEntityBounding.getBoundingInfo().intersectsPoint(checkPos.add(BABYLON.Vector3.TransformCoordinates(new BABYLON.Vector3(bb1.extendSize.x + 0.2, 0, -bb1.extendSize.z + 0.2), rotationMatrix)))
                }

                let right = filteredEntityBounding.getBoundingInfo().intersectsPoint(checkPos.add(BABYLON.Vector3.TransformCoordinates(new BABYLON.Vector3(-bb1.extendSize.x - 0.2, 0, 0), rotationMatrix)))
                if (!right) {
                    right = filteredEntityBounding.getBoundingInfo().intersectsPoint(checkPos.add(BABYLON.Vector3.TransformCoordinates(new BABYLON.Vector3(-bb1.extendSize.x - 0.2, 0, bb1.extendSize.z - 0.2), rotationMatrix)))
                }
                if (!right) {
                    right = filteredEntityBounding.getBoundingInfo().intersectsPoint(checkPos.add(BABYLON.Vector3.TransformCoordinates(new BABYLON.Vector3(-bb1.extendSize.x - 0.2, 0, -bb1.extendSize.z + 0.2), rotationMatrix)))
                }

                if (back) {
                    adjacentEntities.push({ direction: "BACK", directionVector: null, entity: filteredEntity })
                }

                if (front) {
                    adjacentEntities.push({ direction: "FRONT", directionVector: null, entity: filteredEntity })
                }

                if (left) {
                    adjacentEntities.push({ direction: "LEFT", directionVector: null, entity: filteredEntity })
                }

                if (right) {
                    adjacentEntities.push({ direction: "RIGHT", directionVector: null, entity: filteredEntity })
                }
            }
        }

        return adjacentEntities;
    }
}